/**
 * ChatHomeView — the AI chat landing page (the `chat` shell app).
 *
 * Floating-canvas layout on the violet chat stage (DESIGN_DOC §1/§6),
 * tuned to read as ONE calm surface: the support panels are translucent
 * washes that blend with the stage (`.chat-home .ws-card` override), and
 * the hero paints no card at all — a bare message stream + composer
 * (`.chat-hero-shell` strips ChatPanel's ws-card, `hideHeader` drops its
 * title row and tab strip; the rail is the only session switcher).
 *
 *   ┌ soft wash ────┐   (bare hero)          ┌ soft washes ─────┐
 *   │ recent        │    serif greeting      │ app squircles /  │
 *   │ conversations │    chat stream         │ projects /       │
 *   │ (flat list)   │    composer            │ recent files     │
 *   └───────────────┘                        └──────────────────┘
 *
 * The rail is RECENT-SESSION-centric: one flat list across all projects,
 * newest first, each row carrying the standard project glyph (workpapan
 * purple — one icon color = one concept) with the project name as a
 * second line. Running turns show a pulsing dot; a session that finishes
 * while unfocused keeps a gold notification dot until clicked.
 *
 * An in-chat file-path click REPLACES the right column with an embedded
 * editor (SingleFileView), widening the column — the editor becomes the
 * hero card while it's open.
 *
 * Sessions are workspace(=project)-scoped, so the rail fans out one list
 * query per recent project; the running indicator comes from ONE
 * tenant-wide busy scan (GET /api/agent/sessions/status) polled while the
 * page is visible.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { LayoutGrid, PanelLeft, Sparkles, FileClock, Star, StarPlus, StarOff, Plus, Upload, FolderUp, Palette } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { HeroChat, useCanAttach } from '@papan/agent/ui'
import { getActiveOrgEmail, getActiveTenantId } from '@papan/api-client'
import { filesApi } from '@papan/files/client'
import { useRecentWorkPreview, useFile } from '@papan/files/client/queries'
import { parseMiniappIcon, parseMiniappIconRev, isMiniappFolder } from '@papan/types'
// A dragged app tile starts the same Papan drag a dragged drive row does.
import { startPapanDrag } from '@papan/core-ui/ui/papanDrag'
import { projectDisplayName, useNewProject, useUploadService, MiniappIconEditorDialog, projectKeys, flattenRecentFiles, useRecentWords, type ProjectItem } from '@papan/files/ui'
import { FileIcon, ErrorBoundary, useConflict, MiniappIcon, FileContextMenu, useWriteStatus, greetingForHour, useAnchoredPosition, PDAppIcon, useToast, useDraftState, DraftScopeProvider, WORKSPACE_SCOPE_ATTR, WORKSPACE_LOADED_ATTR, WORKSPACE_SINGLE_ATTR } from '@papan/core-ui/ui'
import { selectionFingerprint, revealLineRequest, type EditorSelection, type EditorEditHandle, type RevealLineRequest } from '@papan/collab/client'
import { useTranslation } from '@papan/i18n'
import { usePageTitle } from '@papan/brand/react'
import { SingleFileView } from '../SingleFileView'
import { ResizeHandle } from '../ResizeHandle'
import { useOpenConversationFromUrl } from './useOpenConversationFromUrl'
import { useChatHomeSessions } from './useChatHomeSessions'
import { ConversationList } from './ConversationList'
import { useIsNarrowChatHome } from './useIsNarrowChatHome'
import { ChatHomePageSwitch } from './ChatHomePageSwitch'

// Edge-drag tunables — IDENTICAL to WorkspaceView's so the two surfaces
// feel the same. Collapse: rubber-band below min (slope-1 exponential),
// snap hidden past SNAP_OVER, pull back inside RESTORE_OVER to reopen
// WITHOUT releasing. Unhide (drag starts collapsed): snap in past
// UNHIDE_SNAP, re-hide under UNHIDE_HIDE. Everything commits on release.
const STRETCH = 72
const SNAP_OVER = 108
const RESTORE_OVER = 72
const UNHIDE_SNAP = 72
const UNHIDE_HIDE = 44
const UNHIDE_BUDGE = 24
// Page inset carried on the PANELS' outer margins (not container padding),
// so a collapsed panel takes its inset with it and the handle lands flush
// at the freed edge — same trick as WorkspaceView's PANEL_INSET.
const PANEL_INSET = 16
const COLLAPSE_TRIM = -2

/** WorkspaceView's edge-panel drag loop, ported whole: one gesture handles
 *  resize, drag-to-close AND drag-back-in with hysteresis in BOTH
 *  directions — the snap is reversible until pointer-up. While pulling a
 *  hidden panel back out, the panel stays hidden and the HERO budges
 *  toward its landing spot as the pre-snap tell (quartic magnet ramp,
 *  capped at UNHIDE_BUDGE px). Width + margins are written directly to
 *  the elements during the drag; React state commits once on release. */
function startEdgeDrag(
  e: { clientX: number; preventDefault: () => void },
  cfg: {
    el: HTMLElement | null
    /** +1 = panel grows as the pointer moves right; -1 = left. */
    dir: 1 | -1
    min: number
    max: number
    startWidth: number
    fromCollapsed: boolean
    /** Zero border/shadow while the live width is 0 (ws-card panels). */
    collapsedLook?: boolean
    /** Which outer margin carries the page inset ('left' for a left-docked
     *  panel). Animated with the collapsed look during the drag. */
    insetSide?: 'left' | 'right'
    /** The hero element to budge during an unhide pull. */
    budgeEl?: HTMLElement | null
    onStart: () => void
    onEnd: () => void
    commit: (state: { collapsed: boolean; width: number }) => void
  },
) {
  e.preventDefault()
  cfg.onStart()
  const el = cfg.el
  if (el) el.style.transition = 'none'
  const startX = e.clientX
  let latest = cfg.startWidth
  let collapsed = cfg.fromCollapsed

  const paint = (w: number) => {
    if (!el) return
    el.style.width = `${w}px`
    const lookCollapsed = w <= 0
    if (cfg.collapsedLook) {
      el.style.borderWidth = lookCollapsed ? '0' : ''
      el.style.boxShadow = lookCollapsed ? 'none' : ''
    }
    if (cfg.insetSide === 'left') {
      el.style.marginLeft = lookCollapsed ? '0px' : `${PANEL_INSET}px`
      el.style.marginRight = lookCollapsed ? `${COLLAPSE_TRIM}px` : ''
    } else if (cfg.insetSide === 'right') {
      el.style.marginRight = lookCollapsed ? '0px' : `${PANEL_INSET}px`
      el.style.marginLeft = lookCollapsed ? `${COLLAPSE_TRIM}px` : ''
    }
  }
  const setBudge = (px: number) => {
    if (!cfg.budgeEl) return
    if (cfg.insetSide === 'right') cfg.budgeEl.style.marginRight = px ? `${px}px` : ''
    else cfg.budgeEl.style.marginLeft = px ? `${px}px` : ''
  }
  const teardown = () => {
    cfg.onEnd()
    document.removeEventListener('pointermove', onMove)
    document.removeEventListener('pointerup', onUp)
    document.removeEventListener('pointercancel', onUp)
    if (el) el.style.transition = ''
    setBudge(0)
  }
  const onMove = (ev: PointerEvent) => {
    const desired = cfg.startWidth + cfg.dir * (ev.clientX - startX)
    let budge = 0
    if (cfg.fromCollapsed) {
      // Unhide: the panel stays hidden while you pull; the hero budges
      // toward its landing spot (quartic — stays put through the first
      // half, ramps hard near the threshold: reads as a magnet grab),
      // then the panel SNAPS in past UNHIDE_SNAP (re-hides below
      // UNHIDE_HIDE — hysteresis) and tracks the pointer.
      const d = Math.max(0, desired)
      if (!collapsed && d < UNHIDE_HIDE) collapsed = true
      else if (collapsed && d >= UNHIDE_SNAP) collapsed = false
      if (collapsed) {
        latest = 0
        budge = UNHIDE_BUDGE * Math.min(1, d / UNHIDE_SNAP) ** 4
      } else {
        latest = Math.min(cfg.max, Math.max(cfg.min, d))
      }
    } else if (desired < cfg.min) {
      const over = cfg.min - desired
      if (!collapsed && over >= SNAP_OVER) collapsed = true
      else if (collapsed && over <= RESTORE_OVER) collapsed = false
      latest = collapsed ? 0 : cfg.min - STRETCH * (1 - Math.exp(-over / STRETCH))
    } else {
      collapsed = false
      latest = Math.min(cfg.max, desired)
    }
    paint(latest)
    setBudge(budge)
  }
  const onUp = () => {
    teardown()
    if (collapsed) {
      cfg.commit({ collapsed: true, width: Math.max(cfg.min, cfg.startWidth) })
      return
    }
    const finalW = Math.max(cfg.min, Math.min(cfg.max, latest))
    paint(finalW) // CSS transition animates any snap-back
    cfg.commit({ collapsed: false, width: finalW })
  }
  document.addEventListener('pointermove', onMove)
  document.addEventListener('pointerup', onUp)
  document.addEventListener('pointercancel', onUp)
}

interface Props {
  tenantId: string
  urlPrefix: string
  orgEmail?: string | null
  userName?: string
  /** Organization name — shown above the greeting. Omit on personal tenants. */
  orgName?: string | null
  /** Is the chat deck on screen? It stays MOUNTED when hidden. */
  deckActive?: boolean
}

export function ChatHomeView({ tenantId, urlPrefix, orgEmail, userName, orgName, deckActive = true }: Props) {
  const { t: tCore } = useTranslation('core-ui')
  // The taskbar's own name for this surface. An embedded file overrides it
  // from higher in the stack when one is open.
  usePageTitle(tCore('taskBar.chat'), deckActive)
  const navigate = useNavigate()
  const newProject = useNewProject()
  const toast = useToast()
  // Identity-only (0175). See `ChatShortcuts` for why this is read here rather
  // than threaded, and why the answer is to hide rather than disable.
  const identityOnly = useWriteStatus().identityOnly

  // Data spine shared with the mobile chat screen (useChatHomeSessions.ts).
  const {
    allProjects, driveAssistant, projectsPending, driveAssistantPending,
    miniapps, shortcutProjects,
    sessionGroups, busyIds, doneIds,
    selected, activeProject, activeSessionId,
    openSession, renameSession, deleteSession, refreshSessionLists,
  } = useChatHomeSessions()

  useOpenConversationFromUrl({ allProjects, driveAssistant, projectsPending, driveAssistantPending, openSession, urlPrefix, navigate })
  // Attaching follows the SERVER's answer for the active project — the upload route's own `mayWriteWorkspace`
  // (#1053), never the tenant role: a Viewer with Edit on the project may attach, a Read-only Member may not.
  const canAttach = useCanAttach(activeProject?.id, orgEmail)

  // ── Right column: editor takeover ───────────────────────────────────
  // Shared with the phone chat (#897, `chat:openFile`): a phone/desktop layout
  // swap reopens the same file there, so its editor's unsaved draft survives.
  const [editorFileId, setEditorFileId] = useDraftState<string | null>('chat:openFile', null)
  // Editor unsaved state, bubbled up from SingleFileView, so the hero
  // context chip can badge it. Reset whenever the open file changes.
  const [editorDirty, setEditorDirty] = useState(false)
  useEffect(() => { setEditorDirty(false) }, [editorFileId])

  // Editor selection → AI context chip, mirroring WorkspaceView's project chat.
  // The embedded editor (SingleFileView) forwards its handle here; on a
  // selection change we re-pull getSelection() and feed HeroChat's `selection`.
  const editorRef = useRef<EditorEditHandle | null>(null)
  const editorContainerRef = useRef<HTMLDivElement | null>(null)
  const [editorSelection, setEditorSelection] = useState<EditorSelection | null>(null)
  const lastSelKeyRef = useRef('')
  const selTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => { setEditorSelection(null); lastSelKeyRef.current = '' }, [editorFileId])
  useEffect(() => {
    if (!editorFileId) return
    // `focusGated`: a text selectionchange only counts while focus is in the
    // editor panel (so selecting text in the chat input doesn't steal it). A
    // DESIGN element pick is explicit — the clicked element lives in a
    // cross-origin sandbox iframe whose focus the container check can miss, so
    // it must NOT be focus-gated, or mini-app design selections never register.
    const pull = (focusGated: boolean) => {
      clearTimeout(selTimerRef.current)
      selTimerRef.current = setTimeout(() => {
        if (focusGated && !editorContainerRef.current?.contains(document.activeElement)) return
        const sel = editorRef.current?.getSelection?.() ?? null
        // ONE shared fingerprint (includes cellRange + sheetName + full text, which the
        // old key dropped — an A1->B2 move between equal-length values was deduped away).
        // #617 finding 4.
        const key = selectionFingerprint(sel)
        if (key === lastSelKeyRef.current) return
        lastSelKeyRef.current = key
        setEditorSelection(sel)
      }, 150)
    }
    const onText = () => pull(true)
    const onDesign = () => pull(false)
    document.addEventListener('selectionchange', onText)
    window.addEventListener('papan:editor-selection-change', onDesign)
    return () => {
      document.removeEventListener('selectionchange', onText)
      window.removeEventListener('papan:editor-selection-change', onDesign)
      clearTimeout(selTimerRef.current)
    }
  }, [editorFileId])

  // ── Panel widths + drag-to-close (workspace mechanics: direct-DOM width
  //    during the drag, rubber-band past min, snap closed; restore tabs) ──
  const railRef = useRef<HTMLElement | null>(null)
  const sideRef = useRef<HTMLElement | null>(null)
  const heroRef = useRef<HTMLElement | null>(null)
  const [railWidth, setRailWidth] = useState(272)
  const [railCollapsed, setRailCollapsed] = useState(false)
  const [sideWidth, setSideWidth] = useState(316)
  const [sideCollapsed, setSideCollapsed] = useState(false)
  const [editorWidth, setEditorWidth] = useState(680)
  const [draggingHandle, setDraggingHandle] = useState<'rail' | 'side' | null>(null)

  // ── Single column below 950px (useIsNarrowChatHome) ────────────────
  // Three columns left the hero 230px wide at 950px and 80px at 800px. Below
  // the threshold the rail and the right column leave the row and open as
  // overlay drawers from their own sides, and an open editor covers the hero
  // instead of widening a column. Going back to three columns drops a drawer.
  const narrow = useIsNarrowChatHome()
  const [drawer, setDrawer] = useState<'rail' | null>(null)
  useEffect(() => { if (!narrow) setDrawer(null) }, [narrow])
  useEffect(() => {
    if (!drawer) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawer(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawer])
  // Narrow shows ONE of two pages, like the phone: the chat, or the right
  // column's content (shortcuts, or the open file). Opening a file shows it.
  const [narrowPage, setNarrowPage] = useState<'chat' | 'files'>('chat')

  // Wide: opening a file widens the right column to the editor. When that
  // would leave the chat narrower than this, the conversations rail steps aside
  // — and returns when the file closes, unless the user moved the rail
  // themselves in between (then it stays where they put it).
  const MIN_HERO_WITH_EDITOR = 600
  const railAutoCollapsedRef = useRef(false)
  // #249: the line a chat link (`app.py:42`) asked for, kept with the file it is for.
  const [editorReveal, setEditorReveal] = useState<{ fileId: string; req: RevealLineRequest } | null>(null)
  const openEditor = (fileId: string) => {
    setEditorReveal(null) // handleOpenPath sets its own line after this
    if (narrow) {
      setNarrowPage('files')
    } else if (!editorFileId && !railCollapsed && heroRef.current) {
      const sideNow = sideCollapsed ? 0 : sideWidth
      const editorTarget = Math.min(editorWidth, Math.round(window.innerWidth * 0.7))
      // <main> is px-2 around a max-820 column.
      const heroAfter = Math.min(820, heroRef.current.clientWidth - 16 + sideNow - editorTarget)
      if (heroAfter < MIN_HERO_WITH_EDITOR) {
        setRailCollapsed(true)
        railAutoCollapsedRef.current = true
      }
    }
    setEditorFileId(fileId)
  }
  const closeEditor = () => {
    setEditorFileId(null)
    setEditorReveal(null)
    if (railAutoCollapsedRef.current) {
      railAutoCollapsedRef.current = false
      setRailCollapsed(false)
    }
  }
  // handleOpenPath is a stable callback; it reaches the latest openEditor here.
  const openEditorRef = useRef(openEditor)
  openEditorRef.current = openEditor
  const reopenRail = () => { railAutoCollapsedRef.current = false; setRailCollapsed(false) }
  const reopenSide = () => setSideCollapsed(false)

  const dragChrome = (which: 'rail' | 'side') => ({
    onStart: () => { setDraggingHandle(which); document.body.style.cursor = 'col-resize' },
    onEnd: () => { setDraggingHandle(null); document.body.style.cursor = '' },
  })

  const beginRailDrag = (e: React.PointerEvent) => startEdgeDrag(e, {
    el: railRef.current,
    dir: 1,
    min: 220,
    max: Math.min(440, Math.round(window.innerWidth * 0.32)),
    startWidth: railCollapsed ? 0 : railWidth,
    fromCollapsed: railCollapsed,
    collapsedLook: true,
    insetSide: 'left',
    budgeEl: heroRef.current,
    ...dragChrome('rail'),
    commit: ({ collapsed, width }) => {
      // A manual move wins over the auto-collapse: don't reopen it on close.
      railAutoCollapsedRef.current = false
      setRailCollapsed(collapsed)
      if (!collapsed) setRailWidth(width)
    },
  })

  const beginSideDrag = (e: React.PointerEvent) => startEdgeDrag(e, editorFileId
    ? {
        // Editor open: resize it; dragging it shut CLOSES the editor
        // (back to the widget column) — reversible until release.
        el: sideRef.current,
        dir: -1,
        min: 380,
        max: Math.min(960, Math.round(window.innerWidth * 0.7)),
        startWidth: editorWidth,
        fromCollapsed: false,
        insetSide: 'right',
        budgeEl: heroRef.current,
        ...dragChrome('side'),
        commit: ({ collapsed, width }) => {
          if (collapsed) closeEditor()
          else setEditorWidth(width)
        },
      }
    : {
        el: sideRef.current,
        dir: -1,
        min: 240,
        max: 420,
        startWidth: sideCollapsed ? 0 : sideWidth,
        fromCollapsed: sideCollapsed,
        insetSide: 'right',
        budgeEl: heroRef.current,
        ...dragChrome('side'),
        commit: ({ collapsed, width }) => {
          setSideCollapsed(collapsed)
          if (!collapsed) setSideWidth(width)
        },
      })

  const handleOpenPath = useCallback(async (path: string, at?: { line?: number }) => {
    try {
      const result = await filesApi.resolvePath(path)
      if (!result?.id) {
        toast.error(`File not found: ${path}`)
        return
      }
      if (result.file.isFolder) {
        // A `.miniapp` folder opens in the side-panel editor (Run/Design/
        // Settings) — same as clicking its app pill and how regular files open
        // from chat home — instead of the full-screen app window.
        if (isMiniappFolder(result.file.name)) openEditorRef.current(result.id)
        else navigate(`${urlPrefix}/folder/${result.id}`)
      } else {
        openEditorRef.current(result.id)
        setEditorReveal(at?.line && at.line > 0 ? { fileId: result.id, req: revealLineRequest(at.line) } : null)
      }
    } catch {
      toast.error(`Could not open: ${path}`)
    }
  }, [navigate, urlPrefix, toast])

  // The side-panel file becomes the hero composer's context chip. `useFile`
  // shares the file-detail cache, so opening from recents/a conversation link
  // (which already primed it) is a cache hit. Folders never reach the panel
  // (handleOpenPath navigates instead), but guard anyway.
  const { data: editorFile } = useFile(editorFileId)
  const heroActiveTab = useMemo(() => {
    // A `.miniapp` is a FOLDER but opens as an editor (MiniAppEditor), so allow
    // it — otherwise HeroChat's context chip (gated on activeTab) never renders
    // and a mini-app design selection can't show. Plain folders have no chip.
    if (!editorFile || (editorFile.isFolder && !isMiniappFolder(editorFile.name))) return null
    const name = editorFile.name
    const dot = name.lastIndexOf('.')
    const fileType = dot > 0 ? name.slice(dot + 1).toLowerCase() : 'txt'
    return { fileId: editorFile.id, fileName: name, filePath: editorFile.path, fileType }
  }, [editorFile])

  // ── Greeting ─────────────────────────────────────────────────────────
  const greeting = greetingForHour(new Date().getHours())
  const firstName = userName?.trim().split(/\s+/)[0]

  // The right column's two bodies, rendered in the column (three columns) or in
  // an overlay (narrow). `onPicked` lets the shortcuts drawer close on a pick.
  /** `visible` is NOT the same as mounted here. The narrow layout keeps this
   *  column mounted under `display:none` so the editor survives page switches,
   *  which means a file can be alive while the user is reading the chat. Two of
   *  SingleFileView's effects are document-global — the Ctrl/Cmd+S handler and
   *  the page-title entry — so an off-screen editor would otherwise answer the
   *  save shortcut typed into the composer and keep naming the browser tab. */
  const renderEditor = (fileId: string, visible = true) => (
    // Marked with the chat's project: HeroChat's agent tools are bound to it
    // (#352), and this side editor is where its file opens. The SELECTED
    // project, not the one the open file came from, by design: the editor sits
    // beside — and is what the user is working on with — the chat on screen,
    // and the message's file chip still names the exact file.
    <div ref={editorContainerRef} className="ws-card-hero flex-1 flex flex-col min-w-0 min-h-0" {...{ [WORKSPACE_SCOPE_ATTR]: activeProject?.id, [WORKSPACE_LOADED_ATTR]: '1', [WORKSPACE_SINGLE_ATTR]: '1' }}>
      <ErrorBoundary>
        <DraftScopeProvider scope="chat">
        <SingleFileView
          key={fileId}
          tenantId={tenantId}
          fileId={fileId}
          editorRef={editorRef}
          onClose={closeEditor}
          onSwitchFile={setEditorFileId}
          onDirtyChange={setEditorDirty}
          deckActive={deckActive && visible}
          revealLine={editorReveal?.fileId === fileId ? editorReveal.req : undefined}
        />
        </DraftScopeProvider>
      </ErrorBoundary>
    </div>
  )
  // `visible`: the panel holding them is on screen (not a collapsed side panel, not the narrow layout's
  // hidden Files page) — off screen their Recent files read is paused.
  const renderShortcuts = (visible: boolean) => (
    <ChatShortcuts
      fill
      miniapps={miniapps}
      projects={shortcutProjects}
      onOpenApp={p => navigate(`${urlPrefix}/app/${p.folderId}`)}
      onOpenProject={p => navigate(`${urlPrefix}/workspace/${p.folderId}`)}
      onNewApp={() => newProject.open({ entityKind: 'miniapp', onDone: folderId => navigate(`${urlPrefix}/app/${folderId}`) })}
      onNewProject={() => newProject.open({ onDone: folderId => navigate(`${urlPrefix}/workspace/${folderId}`) })}
      onOpenFile={openEditor}
      active={deckActive && visible}
    />
  )

  return (
    // Side insets live on the PANELS' margins (workspace recipe), NOT here —
    // a collapsed panel takes its inset with it, so the handle sits flush
    // at the freed screen edge. gap-1 = the workspace row's 4px handle gaps.
    // `chat-home` is the SURFACE — attached the moment this view mounts, with
    // no data dependency. The no-flash e2e (e2e/landing-switch.spec.ts) watches
    // for it with a MutationObserver to prove the wrong landing never painted,
    // which the rail/hero ids inside cannot answer as cleanly.
    <div
      data-testid="chat-home"
      className={`chat-home relative flex-1 flex min-w-0 min-h-0 pt-1 overflow-hidden ${narrow ? 'flex-col' : 'gap-1 pb-4'}`}
    >

      {/* ── Conversations rail (grouped by project, recency-ranked) ──
          Stays MOUNTED at width 0 when collapsed so the edge handle can
          drag it back in (workspace unhide mechanics); border/shadow are
          zeroed so no sliver of card remains. */}
      {/* Narrow: the rail leaves the row and opens as a drawer (end of view). */}
      {!narrow && (
        <>
          <aside
            ref={railRef as React.RefObject<HTMLElement>}
            className="ws-card ws-panel-collapsible shrink-0 flex flex-col min-h-0"
            style={railCollapsed
              ? { width: 0, marginLeft: 0, marginRight: COLLAPSE_TRIM, borderWidth: 0, boxShadow: 'none' }
              : { width: railWidth, marginLeft: PANEL_INSET }}
            data-testid="chat-home-rail"
          >
            <ConversationList
              sessionGroups={sessionGroups}
              busyIds={busyIds}
              doneIds={doneIds}
              activeProject={activeProject}
              activeSessionId={activeSessionId}
              openSession={openSession}
              renameSession={renameSession}
              deleteSession={deleteSession}
              urlPrefix={urlPrefix}
            />
          </aside>
          <ResizeHandle onPointerDown={beginRailDrag} dragging={draggingHandle === 'rail'} visibleAtRest />
        </>
      )}

      {/* ── Hero: the chat, floating bare on the stage ─────────────── */}
      <main
        ref={heroRef as React.RefObject<HTMLElement>}
        className="flex-1 min-w-0 flex flex-col min-h-0 items-center px-2"
        // Narrow Files page: hidden, not unmounted, so the conversation and a
        // half-typed prompt survive switching pages.
        style={narrow && narrowPage === 'files' ? { display: 'none' } : undefined}
      >
        <div className="w-full max-w-[820px] flex-1 flex flex-col min-h-0">
          {/* Org label is ABSOLUTE inside the fixed-padding header so the
              greeting sits at the same y with or without it (no jump when
              switching to a personal tenant). */}
          {/* `landing-greeting` / `landing-orgname` are shared with
              LauncherView's copy of this header, so one spec can assert the
              greeting on whichever surface the deployment lands on. See the
              note there. */}
          <div className="shrink-0 relative pt-7 pb-3 text-center">
            {/* Reopen buttons. Narrow: the rail lives behind the left one (the
                right column is the Files page of the bottom switch). Wide: each
                appears only while its column is collapsed. Absolute, so the
                greeting keeps its centre and its y. */}
            {(narrow || railCollapsed) && (
              <button
                onClick={() => { if (narrow) setDrawer('rail'); else reopenRail() }}
                aria-label="Conversations"
                title="Conversations"
                aria-expanded={narrow ? drawer === 'rail' : undefined}
                className="absolute left-0 top-2 z-10 p-2 rounded-lg themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
              >
                <PanelLeft size={18} />
              </button>
            )}
            {!narrow && sideCollapsed && !editorFileId && (
              <button
                onClick={reopenSide}
                aria-label="Shortcuts"
                title="Shortcuts"
                className="absolute right-0 top-2 z-10 p-2 rounded-lg themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
              >
                <LayoutGrid size={18} />
              </button>
            )}
            {orgName && (
              <p
                className="chat-home-orgname select-none absolute top-0 inset-x-0 truncate px-8"
                data-testid="landing-orgname"
              >
                {orgName}
              </p>
            )}
            <h1
              className="chat-home-greeting text-[30px] leading-tight themed-text"
              data-testid="landing-greeting"
            >
              {greeting}{firstName ? `, ${firstName}` : ''}
            </h1>
          </div>
          <div className="chat-hero-shell flex-1 min-h-0" data-testid="chat-home-hero">
            {activeProject ? (
              <ErrorBoundary>
                <HeroChat
                  key={activeProject.id}
                  workspaceId={activeProject.id}
                  workspaceFolderId={activeProject.folderId}
                  orgEmail={orgEmail ?? undefined}
                  canWrite={canAttach}
                  onOpenBilling={() => navigate(`${urlPrefix}/settings/subscription`)}
                  onManageChatStorage={() => navigate(`${urlPrefix}/settings/storage`)}
                  sessionId={selected?.project.id === activeProject.id ? activeSessionId : null}
                  onSessionCreated={(sid) => {
                    openSession(activeProject, sid)
                    refreshSessionLists(activeProject.id)
                  }}
                  onTurnComplete={() => refreshSessionLists()}
                  onOpenFile={handleOpenPath}
                  activeTab={heroActiveTab}
                  selection={editorSelection}
                  unsaved={editorDirty}
                />
              </ErrorBoundary>
            ) : (
              <div className="h-full flex flex-col items-center justify-center gap-3 themed-text-muted">
                <Sparkles size={32} className="text-purple-400/70" />
                {/* A conversation needs a project, and a project needs a drive.
                    Inviting an identity-only person to make one sends them to a
                    screen where every action 403s — so this says where
                    conversations DO live for them instead. */}
                {identityOnly ? (
                  <p className="text-sm max-w-xs text-center">
                    Conversations live in an organization. Switch to one from the
                    account menu to start chatting.
                  </p>
                ) : (
                  <>
                    <p className="text-sm">Create a project to start your first conversation.</p>
                    <button
                      onClick={() => navigate(`${urlPrefix}/projects`)}
                      className="ws-tool-btn ws-tool-btn-accent text-sm"
                    >
                      Open Projects
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* ── Right column: shortcuts + widgets ⇄ embedded editor ──────
          Same unhide contract as the rail: mounted at width 0 when
          collapsed, handle always present for the drag-back-in. */}
      {!narrow && (
        <>
          <ResizeHandle onPointerDown={beginSideDrag} dragging={draggingHandle === 'side'} visibleAtRest />
          <aside
            ref={sideRef as React.RefObject<HTMLElement>}
            className="ws-panel-collapsible shrink-0 flex flex-col min-h-0"
            style={editorFileId
              ? { width: Math.min(editorWidth, Math.round(window.innerWidth * 0.7)), marginRight: PANEL_INSET }
              : sideCollapsed
                ? { width: 0, marginRight: 0, marginLeft: COLLAPSE_TRIM }
                : { width: sideWidth, marginRight: PANEL_INSET }}
            data-testid="chat-home-side"
          >
            {editorFileId ? renderEditor(editorFileId) : renderShortcuts(!sideCollapsed)}
          </aside>
        </>
      )}

      {/* Narrow Files page — the right column's content as a page of its own:
          the shortcuts, or the open file in their place. Closing the file shows
          the shortcuts again; the switch below stays on screen either way. */}
      {narrow && (
        <div
          className="flex-1 min-h-0 flex flex-col px-2 pt-1"
          data-testid="chat-home-files-page"
          // Hidden, not unmounted — exactly what the hero above does, for the
          // same reason. Unmounting re-mounted SingleFileView on every page
          // switch: refetching the file, rejoining the collab room, and losing
          // scroll position, image zoom and an open version panel each time.
          style={narrowPage === 'files' ? undefined : { display: 'none' }}
        >
          {editorFileId ? renderEditor(editorFileId, narrowPage === 'files') : renderShortcuts(narrowPage === 'files')}
        </div>
      )}
      {narrow && <ChatHomePageSwitch page={narrowPage} onChange={setNarrowPage} />}

      {/* Narrow conversations drawer — the phone's fog scrim plus the rail as a
          card from the left. Scrim tap, Esc or picking a conversation closes it;
          a pick also brings the chat page forward. */}
      {narrow && drawer === 'rail' && (
        <div className="absolute inset-0 z-40">
          <div className="mobile-fog mobile-fog-deep mobile-fog-in absolute inset-0" onClick={() => setDrawer(null)} aria-hidden />
          <aside
            className="mobile-sheet-in absolute inset-y-0 left-0 flex w-[min(320px,85%)] flex-col pb-4 pt-1 pl-2"
            data-testid="chat-home-rail-drawer"
          >
            <div className="ws-card flex-1 flex min-h-0 flex-col">
              <ConversationList
                sessionGroups={sessionGroups}
                busyIds={busyIds}
                doneIds={doneIds}
                activeProject={activeProject}
                activeSessionId={activeSessionId}
                openSession={openSession}
                renameSession={renameSession}
                deleteSession={deleteSession}
                urlPrefix={urlPrefix}
                // `touch`, like the phone twin passes. Without it
                // ConversationList styles the per-row ⋮ as
                // `opacity-0 group-hover/srow:opacity-100` — and this drawer
                // only exists below 950px, which includes touch tablets with no
                // hover at all. Rename and Delete were invisible there, and
                // findable only by guessing where the trigger sits.
                touch
                onSelect={() => { setDrawer(null); setNarrowPage('chat') }}
              />
            </div>
          </aside>
        </div>
      )}
    </div>
  )
}

/** The shortcuts stack — app squircles, project rows, recent files. Shared
 *  by the desktop right column and the mobile shortcuts drawer; the host
 *  decides what opening each thing means (side-panel takeover vs navigate). */
export function ChatShortcuts({ miniapps, projects, onOpenApp, onOpenProject, onNewApp, onNewProject, onOpenFile, fill, active }: {
  miniapps: ProjectItem[]
  projects: ProjectItem[]
  onOpenApp: (p: ProjectItem) => void
  onOpenProject: (p: ProjectItem) => void
  onNewApp: () => void
  onNewProject: () => void
  onOpenFile: (fileId: string) => void
  /** Is the surface on screen? False pauses the Recent files read (a hidden deck). */
  active?: boolean
  /** `fill` (mobile page): the whole stack fits the viewport instead of
   *  scrolling as one column — Apps stays fixed, Projects and Recent become
   *  flex-shrinkable panels that scroll internally. */
  fill?: boolean
}) {
  const queryClient = useQueryClient()

  /**
   * Identity-only (0175): this tenant has no drive, so every CREATE below is a
   * button that can only 403.
   *
   * Read from `useWriteStatus` rather than threaded as a prop — the same shape
   * `HeroChat` already uses for `uploadsPaused`, and the only signal reachable
   * from `features/workspace` (it may not import the shell). It answers for the
   * ACTIVE tenant, which is exactly whose drive these controls would write to.
   *
   * Hiding, not disabling: a disabled control invites "who do I ask to enable
   * this", and the answer is that this account does not have a drive at all.
   * The OPEN paths stay — an identity-only person can still be shared into
   * someone else's app, and the tiles that list them must keep working.
   *
   * `false` until the first response, so a full tenant never flickers; the
   * server refuses regardless, exactly as with every other surface here.
   */
  const identityOnly = useWriteStatus().identityOnly

  // Right-click menu on an app tile — mirrors the drive surfaces' miniapp
  // context menus (currently just "Edit icon"; open stays the tile's click).
  // `anchorEl` rather than coordinates — one trigger per app tile, so there is
  // no single ref to hold; the element is what the shared convention follows
  // and observes for exit.
  const [appMenu, setAppMenu] = useState<
    { app: ProjectItem; anchorEl: HTMLElement; offX: number; offY: number } | null
  >(null)
  const appMenuAnchorRef = useRef<HTMLElement | null>(null)
  appMenuAnchorRef.current = appMenu?.anchorEl ?? null
  const appMenuRef = useRef<HTMLDivElement>(null)
  const [iconTarget, setIconTarget] = useState<ProjectItem | null>(null)
  useEffect(() => {
    if (!appMenu) return
    const onDoc = (e: MouseEvent) => {
      if (appMenuRef.current?.contains(e.target as Node)) return
      setAppMenu(null)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [appMenu])

  // Position against the CLICK POINT carried inside the tile, so the menu opens
  // where the pointer was while still following (and exiting with) the tile.
  const appMenuRect = useCallback((el: HTMLElement) => {
    const r = el.getBoundingClientRect()
    const x = r.left + (appMenu?.offX ?? 0)
    const y = r.top + (appMenu?.offY ?? 0)
    return { top: y, left: x, right: x, bottom: y, width: 0, height: 0 }
  }, [appMenu?.offX, appMenu?.offY])

  // Shared anchored-panel convention: follow the tile, play out when it leaves.
  const { pos: appMenuPos, exiting: appMenuExiting, origin: appMenuOrigin } = useAnchoredPosition({
    open: !!appMenu,
    anchorRef: appMenuAnchorRef,
    floatingRef: appMenuRef,
    onDismiss: useCallback(() => setAppMenu(null), []),
    // Same as SharedView: one ref, one ⋮ per tile — right-clicking another
    // tile repoints it with the menu still open.
    anchorKey: appMenu?.app.id ?? null,
    anchorRect: appMenuRect,
    gap: 0,
    exitTransitionMs: 160,
  })

  return (
    // All three panels stay mounted even when empty — each shows a create /
    // upload affordance so a brand-new drive has an obvious way to start.
    <div className={`flex-1 min-h-0 flex flex-col gap-3 ${fill ? '' : 'overflow-y-auto overflow-x-hidden'}`}>
      <section className="ws-card p-3.5 shrink-0">
        <div className="ws-home-heading pb-3">
          Apps
          {miniapps.length > 0 && !identityOnly && (
            <button
              onClick={onNewApp}
              className="ml-auto -my-1 p-1 rounded-md themed-text-muted hover:themed-text transition-colors"
              data-tooltip="Create app"
              aria-label="Create app"
            >
              <Plus size={14} />
            </button>
          )}
        </div>
        {miniapps.length > 0 ? (
          <div className="flex flex-wrap gap-x-3 gap-y-2.5">
            {miniapps.map(p => {
              const name = projectDisplayName(p.name)
              return (
                <button
                  key={p.id}
                  onClick={() => onOpenApp(p)}
                  // Draggable onto the taskbar rail, which pins the app into the
                  // slot it lands in — the same file payload the drive's rows
                  // carry, so the rail has one drop path for both.
                  draggable
                  onDragStart={e => {
                    e.dataTransfer.effectAllowed = 'link'
                    // Names its drive, so another drive's rail (another tab) refuses it.
                    startPapanDrag(e.dataTransfer, { tenantId: getActiveTenantId() || undefined, files: [{ id: p.folderId, name: p.name, isFolder: true }] })
                  }}
                  onContextMenu={e => {
                    // Edit icon is a write — only offer the menu to managers.
                    if (!p.access?.canManage) return
                    e.preventDefault()
                    // A right-click menu belongs AT THE POINTER; the tile is
                    // only what it tracks and exits with. Keep the click point
                    // as an offset inside the tile so it rides along — the
                    // same split DriveView's right-click menu uses.
                    const el = e.currentTarget as HTMLElement
                    const r = el.getBoundingClientRect()
                    setAppMenu({ app: p, anchorEl: el, offX: e.clientX - r.left, offY: e.clientY - r.top })
                  }}
                  className="flex flex-col items-center gap-1 w-[54px] min-w-0"
                  title={name}
                >
                  {/* The stored spec (or a name-derived default) rendered as the
                      shared squircle — replaces the old hue-gradient tile. */}
                  <MiniappIcon spec={parseMiniappIcon(p.metadataJson)} name={name} size={46} refId={p.folderId} rev={parseMiniappIconRev(p.metadataJson)} className="chat-app-tile shrink-0" />
                  <span className="w-full truncate text-center text-[10px] themed-text-muted">{name}</span>
                </button>
              )
            })}
          </div>
        ) : identityOnly ? (
          // No drive to build one IN. Says what this account is rather than
          // leaving a blank card, which reads as a load that failed.
          <p className="text-[13px] themed-text-muted">
            Apps shared with you appear here.
          </p>
        ) : (
          // Same row as the Projects card's "Create a project" CTA — one
          // empty-state shape across the two panels. The glyph keeps the APP
          // pink (one icon colour = one concept), only the layout is shared.
          <button onClick={onNewApp} className="ws-snav-item !py-2.5 text-left">
            <Plus size={14} className="chat-app-icon shrink-0" aria-hidden />
            <span className="flex-1 min-w-0 truncate text-[13px] text-left themed-text-secondary">Create your first PDApp</span>
          </button>
        )}
        {appMenu && (
          <FileContextMenu
            x={appMenuPos?.left ?? -9999}
            y={appMenuPos?.top ?? -9999}
            exiting={appMenuExiting}
            origin={appMenuOrigin}
            menuRef={appMenuRef}
            items={[
              { kind: 'item', icon: Palette, label: 'Edit icon', onClick: () => { setIconTarget(appMenu.app); setAppMenu(null) } },
            ]}
          />
        )}
        {iconTarget && (
          <MiniappIconEditorDialog
            file={{ id: iconTarget.folderId, name: iconTarget.name, metadataJson: iconTarget.metadataJson }}
            tenantId={getActiveTenantId() || undefined}
            onClose={() => setIconTarget(null)}
            onSaved={() => {
              queryClient.invalidateQueries({ queryKey: projectKeys.list() })
              queryClient.invalidateQueries({ queryKey: projectKeys.recent() })
            }}
          />
        )}
      </section>

      <section className={`ws-card p-3.5 flex flex-col ${fill ? 'flex-1 min-h-0' : 'shrink-0'}`}>
        <div className="ws-home-heading pb-1.5 shrink-0">
          Projects
          {projects.length > 0 && !identityOnly && (
            <button
              onClick={onNewProject}
              className="ml-auto -my-1 p-1 rounded-md themed-text-muted hover:themed-text transition-colors"
              data-tooltip="Create project"
              aria-label="Create project"
            >
              <Plus size={14} />
            </button>
          )}
        </div>
        <div className={fill ? 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden' : ''}>
          {projects.map(p => {
            const isApp = isMiniappFolder(p.name)
            return (
              <button
                key={p.id}
                onClick={() => (isApp ? onOpenApp(p) : onOpenProject(p))}
                className="ws-snav-item !py-2.5 text-left"
              >
                {isApp
                  ? <PDAppIcon size={14} className="chat-app-icon shrink-0" aria-hidden />
                  : <LayoutGrid size={14} className="chat-proj-icon shrink-0" aria-hidden />}
                <span className="flex-1 min-w-0 truncate text-[13px] text-left">{projectDisplayName(p.name)}</span>
              </button>
            )
          })}
          {projects.length === 0 && (identityOnly ? (
            <p className="text-[13px] themed-text-muted py-2.5">
              Projects live in an organization you belong to.
            </p>
          ) : (
            <button onClick={onNewProject} className="ws-snav-item !py-2.5 text-left">
              <Plus size={14} className="chat-proj-icon shrink-0" aria-hidden />
              <span className="flex-1 min-w-0 truncate text-[13px] text-left themed-text-secondary">Create a project</span>
            </button>
          ))}
        </div>
      </section>

      <RecentFilesWidget onOpen={onOpenFile} fill={fill} active={active} />
    </div>
  )
}

// ── Shooting-star density toggle ───────────────────────────────────────
// Three states cycled by the chat top-bar toggle; persisted + broadcast so
// the toggle (in ChatHomeView) and ShootingStars (mounted on App.tsx's stage
// overlay) stay in sync across the component split.
export type StarMode = 'normal' | 'more' | 'off'
const STAR_MODE_KEY = 'papan-chat-star-mode'
const STAR_MODE_EVENT = 'papan:chat-star-mode'

function readStarMode(): StarMode {
  try {
    const v = localStorage.getItem(STAR_MODE_KEY)
    return v === 'more' || v === 'off' ? v : 'normal'
  } catch { return 'normal' }
}

/** [mode, cycle] — cycle steps normal → more → off → normal, writing through
 *  localStorage and a window event so every subscriber updates at once. */
export function useStarMode(): [StarMode, () => void] {
  const [mode, setMode] = useState<StarMode>(readStarMode)
  useEffect(() => {
    const sync = () => setMode(readStarMode())
    window.addEventListener(STAR_MODE_EVENT, sync)
    return () => window.removeEventListener(STAR_MODE_EVENT, sync)
  }, [])
  const cycle = useCallback(() => {
    setMode(prev => {
      const next: StarMode = prev === 'normal' ? 'more' : prev === 'more' ? 'off' : 'normal'
      try { localStorage.setItem(STAR_MODE_KEY, next) } catch { /* ignore */ }
      window.dispatchEvent(new CustomEvent(STAR_MODE_EVENT))
      return next
    })
  }, [])
  return [mode, cycle]
}

/** Chat top-bar control — cycles the shooting-star density. Icon carries the
 *  state (Star = on, StarPlus = more, StarOff = off); styled like ThemeToggle. */
export function ChatStarToggle() {
  const [mode, cycle] = useStarMode()
  const Icon = mode === 'off' ? StarOff : mode === 'more' ? StarPlus : Star
  const label = mode === 'off' ? 'Shooting stars: off' : mode === 'more' ? 'Shooting stars: more' : 'Shooting stars: on'
  return (
    <button
      onClick={cycle}
      title={label}
      aria-label={label}
      data-testid="chat-star-toggle"
      className="flex items-center justify-center w-6 h-6 rounded-full themed-bg-tertiary themed-text-secondary hover:themed-text transition-colors"
    >
      <Icon size={14} />
    </button>
  )
}

interface StarSpec { id: number; left: number; top: number; angle: number; alpha: number; dist: number; durMs: number; fadeMs: number }

/** Shooting stars — the sfio2 ShootingStarsContainer recipe, CSS-ified.
 *  Two independent chains (start() shoots twice → stars often read as a
 *  pair): each star spawns just ABOVE the viewport at a right-biased x,
 *  flies 135° (top-right → bottom-left) at CONSTANT velocity (duration =
 *  distance × 0.5 ms/px — linear, no easing), alpha 0.5–1 fading linearly
 *  in flight, and each chain reschedules itself after landing. Travel is
 *  sized so the flight ends around 65% of the viewport height (the linear
 *  fade zeroes out by 70% of it), so streaks die above the chat instead
 *  of crossing it. Behind the panels (ws-cards are z-1); nothing renders
 *  under prefers-reduced-motion. */
export function ShootingStars() {
  const [stars, setStars] = useState<Record<number, StarSpec | null>>({})
  const [mode] = useStarMode()

  useEffect(() => {
    if (mode === 'off') { setStars({}); return }
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    // 'more' = twice the concurrent chains + a shorter respawn gap.
    const chains = mode === 'more' ? 4 : 2
    const gapMin = mode === 'more' ? 150 : 400
    const gapRange = mode === 'more' ? 500 : 1200
    let alive = true
    const timers = new Set<ReturnType<typeof setTimeout>>()
    const later = (fn: () => void, ms: number) => {
      const t = setTimeout(() => { timers.delete(t); fn() }, ms)
      timers.add(t)
    }
    const shoot = (chain: number) => {
      if (!alive) return
      const vw = window.innerWidth
      const vh = window.innerHeight
      // sin(135°) ≈ 0.707 → this travel ends around 65% of the viewport
      // height; the reference flies (rand·500 + screenH)·1.5 across the
      // whole screen, ours stops short of the chat.
      const dist = (vh * 0.65 + 20) / 0.707 + Math.random() * 160
      const MS_PER_PX = 3 // constant velocity (reference recipe, retuned slower)
      const durMs = dist * MS_PER_PX
      // The FADE is tied to a screen line, not a flight fraction: alpha
      // reaches 0 as y crosses the top of the chat band (~230–290px), so
      // the streak dims out right where the messages begin.
      const fadeLine = 230 + Math.random() * 60
      const fadeMs = ((fadeLine + 20) / 0.707) * MS_PER_PX
      setStars(prev => ({
        ...prev,
        [chain]: {
          id: Date.now() + chain,
          left: 200 + Math.random() * vw,   // right-biased; may enter from offscreen right
          top: -20,                         // enters from above the viewport
          angle: 135,
          alpha: 0.5 + Math.random() * 0.5,
          dist,
          durMs,
          fadeMs,
        },
      }))
      later(() => {
        if (!alive) return
        setStars(prev => ({ ...prev, [chain]: null }))
        later(() => shoot(chain), gapMin + Math.random() * gapRange)
      }, durMs + 60)
    }
    for (let i = 0; i < chains; i++) shoot(i)
    return () => { alive = false; for (const t of timers) clearTimeout(t) }
  }, [mode])

  const active = Object.values(stars).filter((s): s is StarSpec => !!s)
  if (active.length === 0) return null
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {active.map(star => (
        <span
          key={star.id}
          className="chat-star"
          style={{
            left: star.left,
            top: star.top,
            '--star-angle': `${star.angle}deg`,
            '--star-alpha': star.alpha,
            '--star-dist': `${star.dist}px`,
            '--star-dur': `${star.durMs}ms`,
            '--star-fade': `${star.fadeMs}ms`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  )
}

/** Recent-files widget — clicking a file opens it in the side-panel editor,
 *  the same takeover an in-chat file link triggers. The viewer's own recent
 *  files (#653: the Recent data, groups flattened to files; an app has none). */
function RecentFilesWidget({ onOpen, fill, active = true }: { onOpen: (fileId: string) => void; fill?: boolean; active?: boolean }) {
  // Paused while the chat deck is hidden (only marked stale); reloads when shown.
  const recent = useRecentWorkPreview('mine', { subscribed: active })
  const resolveConflict = useConflict()
  // Uploads land at the drive root (null target) — enough to get a fresh
  // drive going; the file then surfaces in this same list on refetch.
  const upload = useUploadService({ onComplete: () => { void recent.refetch() } })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  // Heading upload menu — fixed-positioned from the button rect (same recipe
  // as ConversationList's row menu) so the panel's scroll can't clip it.
  const [uploadMenu, setUploadMenu] = useState<{ x: number; y: number } | null>(null)
  // The viewer's own space reads "My Space" (as the drive names it).
  const recentWords = useRecentWords(getActiveOrgEmail(), undefined)
  const files = flattenRecentFiles(recent.data?.groups ?? [], 7, recentWords).map(f => ({ id: f.id, name: f.name, parentName: f.placeName }))
  // Identity-only (0175): uploads land at THIS tenant's drive root, which does
  // not exist. Same reasoning as `ChatShortcuts` — hide, don't disable. The
  // LIST stays: the route is gated so it returns nothing here anyway, and if it
  // ever did return rows they would be legitimately openable.
  const identityOnly = useWriteStatus().identityOnly

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    await upload.handleExternalDrop(e, null, resolveConflict)
  }
  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files
    if (!picked || picked.length === 0) return
    await upload.handlePickedFiles(picked, null, resolveConflict)
    e.target.value = ''
  }

  return (
    <section
      className={`ws-card p-3.5 flex flex-col transition-shadow ${fill ? 'flex-1 min-h-0' : 'shrink-0'} ${dragOver ? 'ring-2 ring-purple-400/50' : ''}`}
      // The drop target goes too, not just the buttons — a drag-and-drop that
      // silently 403s after the file has been dropped is worse than one the
      // surface never invited.
      onDragOver={identityOnly ? undefined : e => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={identityOnly ? undefined : e => { if (e.currentTarget === e.target) setDragOver(false) }}
      onDrop={identityOnly ? undefined : onDrop}
    >
      <div className="ws-home-heading pb-1.5 shrink-0">
        <FileClock size={12} aria-hidden />
        Recent files
        {files.length > 0 && !identityOnly && (
          <button
            onClick={e => {
              const r = e.currentTarget.getBoundingClientRect()
              setUploadMenu(prev => (prev ? null : { x: r.right, y: r.bottom }))
            }}
            className="ml-auto -my-1 p-1 rounded-md themed-text-muted hover:themed-text transition-colors"
            data-tooltip="Upload"
            aria-label="Upload"
          >
            <Upload size={13} />
          </button>
        )}
      </div>
      {uploadMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setUploadMenu(null)} />
          <div
            className="ws-menu fixed z-50 min-w-[150px]"
            style={{ left: Math.max(8, uploadMenu.x - 150), top: uploadMenu.y + 4 }}
            role="menu"
          >
            <button
              role="menuitem"
              className="ws-menu-item"
              onClick={() => { setUploadMenu(null); fileInputRef.current?.click() }}
            >
              <Upload size={13} aria-hidden />
              Upload files
            </button>
            <button
              role="menuitem"
              className="ws-menu-item"
              onClick={() => { setUploadMenu(null); folderInputRef.current?.click() }}
            >
              <FolderUp size={13} aria-hidden />
              Upload folder
            </button>
          </div>
        </>
      )}
      <input ref={fileInputRef} type="file" multiple onChange={onPick} className="hidden" />
      {/* Folder picker — browser supplies webkitRelativePath per file so the
          upload pipeline mkdir-p's the tree. OS dialog allows one folder per
          pick; multiple folders at once only via drag & drop. */}
      <input
        ref={folderInputRef}
        type="file"
        multiple
        // @ts-expect-error non-standard but widely supported
        webkitdirectory=""
        directory=""
        onChange={onPick}
        className="hidden"
      />
      {files.length > 0 ? (
        <div className={fill ? 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden' : ''}>
          {files.map(f => (
            <button key={f.id} onClick={() => onOpen(f.id)} className="ws-snav-item !py-1.5 text-left">
              <FileIcon name={f.name} size={15} />
              <span className="flex-1 min-w-0 truncate text-[13px] text-left">{f.name}</span>
              {f.parentName && <span className="text-[10px] themed-text-muted shrink-0 truncate max-w-[70px]">{f.parentName}</span>}
            </button>
          ))}
        </div>
      ) : identityOnly ? (
        <p className="text-[13px] themed-text-muted">
          Files you open in an organization appear here.
        </p>
      ) : (
        <div className="w-full rounded-lg border border-dashed themed-border flex flex-col items-center justify-center gap-2 py-6 px-3">
          <Upload size={18} className="themed-text-muted" aria-hidden />
          <div className="flex items-center gap-2">
            <button onClick={() => fileInputRef.current?.click()} className="ws-tool-btn text-[12px]">Upload files</button>
            <button onClick={() => folderInputRef.current?.click()} className="ws-tool-btn text-[12px]">Upload folder</button>
          </div>
          <span className="text-[11px] themed-text-muted">or drag &amp; drop files or folders</span>
        </div>
      )}
    </section>
  )
}
